<template>
    <div>
        <i v-if="loading" class="loader__loading-icon fa-spin"/>

        <datepicker
            :disabled-dates="allowedDates"
            v-if="allowedDates != null"
            class="mx-auto w-full md:w-5/6"
            calendar-class="gifting__calendar"
            :monday-first="true"
            v-model="date"
            :inline="true"
            @selected="dateSelected"/>
    </div>
</template>

<script>
import { mapActions, mapGetters } from 'vuex';
import moment from 'moment-timezone';
import Datepicker from 'vuejs-datepicker';

export default {
    name: 'DeliveryDay',
    components: {
        Datepicker,
    },
    data() {
        return {
            dateFormat: 'YYYY-MM-DD',
            date: null,
            allowedDates: null,
            disabledDates: [],
            loading: true,
            oneOffGiftsDisabledDates: [
                '2020-04-10',
                '2020-04-13',
            ],
        };
    },
    mounted() {
        this.fetchGiftingUndeliverableDates();
        this.fetchDeliveryDays().then(() => {
            this.loading = false;
            this.disabledDates = this.undeliverableDays;
            this.showCalendar();
        });

        // Recalculate the dates every 15 seconds in the instance the cut off time has passed.
        setInterval(this.populateDates, 15000);
    },
    computed: {
        ...mapGetters({
            deliverableDate: 'gifting/getDeliverableDate',
            hasMultipleDeliveriesSelected: 'gifting/isSubscription',
            deliverableDateText: 'gifting/getDeliverableDateString',
            undeliverableDates: 'cms/getGiftingUndeliverableDates',
            undeliverableDays: 'gifting/getUndeliverableDays',
        }),
        deliveryDay: {
            get() {
                return this.$store.getters['gifting/getDeliveryDateSelected'];
            },
            set(value) {
                this.$store.commit('gifting/setDeliveryDateSelected', value);
            },
        },

        deliverableDatesForSubscription() {
            return [0, 1, 2, 3, 4, 5, 6].filter(date => date !== this.deliverableDate);
        },

        undeliverableDatesForOneOffGifts() {
            return this.undeliverableDates.concat(this.oneOffGiftsDisabledDates.map(dateStr => new Date(dateStr)));
        },
    },
    methods: {
        ...mapActions({
            fetchGiftingUndeliverableDates: 'cms/fetchGiftingUndeliverableDates',
            fetchDeliveryDays: 'gifting/fetchDeliveryDays',
        }),

        showCalendar() {
            this.populateDates();
            this.getInitialDate();
        },

        populateDates() {
            if (this.hasMultipleDeliveriesSelected) {
                this.disabledDates = this.deliverableDatesForSubscription;
            }

            let earliestDate = moment().tz('Europe/Berlin').startOf('day');

            // If it is past noon we need to add an additional day as the cut off has past
            if (moment().tz('Europe/Berlin').hours() >= 12) {
                earliestDate = earliestDate.add(36, 'hours').add(1, 'day');
            } else {
                earliestDate = earliestDate.add(36, 'hours');
            }

            let maxDate = moment().tz('Europe/Berlin').add(2, 'years').startOf('day');

            this.allowedDates = {
                from: maxDate.toDate(),
                to: earliestDate.toDate(),
                days: this.disabledDates,
                dates: this.hasMultipleDeliveriesSelected ? this.undeliverableDates : this.undeliverableDatesForOneOffGifts,
            };
        },

        getInitialDate() {
            if (this.deliveryDay && this.isDeliverableDate(new Date(this.deliveryDay))) {
                // Previously set this date and this date is still valid
                this.date = new Date(this.deliveryDay);
            } else {
                let proposedDate = moment().tz('Europe/Berlin');
                let iterations = 0;
                let maxIterations = 1000;

                // Try to find the date with a max iterations to prevent an infinite loop in the instance
                // something has failed.
                while (iterations < maxIterations) {
                    // Check date is after the allowed cut off time and a day we delivery
                    if (this.isDeliverableDate(proposedDate)) {
                        this.date = proposedDate.toDate();
                        break;
                    }

                    proposedDate.add(1, 'day');
                    iterations++;
                }
            }
        },

        isDeliverableDate(dateStr) {
            let date = moment(dateStr).startOf('day');

            // Check is after the cut off
            if (!date.isAfter(this.allowedDates.to) || this.oneOffGiftsDisabledDates.includes(date.format(this.dateFormat))) {
                return false;
            }

            let isUndeliverableDate = this.undeliverableDates.find(dtStr => {
                return moment(dtStr).startOf('day').isSame(date);
            });

            if (isUndeliverableDate) return false;

            // Check the day number is a deliverable day number
            return !this.allowedDates.days.includes(date.day());
        },

        dateSelected() {
            this.$scrollTo('.scroll-to-text-me', {
                offset: -80,
            });
        },
    },
    watch: {
        date(value) {
            this.deliveryDay = this.date;
        },
    },
};
</script>
